// Armado productizado (docs/PLAN-R1-SOMBRA.md, pieza 2): POST arma la batch
// de rideRequests pendientes de site+fecha+direction y GET lista lo armado un
// dia. Aca vive SOLO el wire; el calculo es de apps/api/src/routing.
import { z } from "zod";
import { Decimal, Integer, CalendarDate, Instant, RecordId, StopId, SiteId, ProviderId, RideRequestId, TripId, } from "./common.js";
import { RideRequestDirection } from "./ride-requests.js";
import { TripStatus } from "./trips.js";
// PB-16 (docs/PLAN-DESPACHO.md): el armado se puede correr sobre un grupo
// tantas veces como haga falta. Solo toma solicitudes pending; las asignadas
// se ignoran (ya no hay 409 por asignadas).
export const RoutingScope = z
    .enum(["closed_groups", "all_pending"])
    .openapi("RoutingScope", {
    description: "Que solicitudes pendientes entran: closed_groups, las de grupos cuyo cierre del despacho ya paso (o sin cierre); all_pending, todas (incluye grupos abiertos). Ausente: all_pending.",
});
export const RoutingRequest = z
    .object({
    date: CalendarDate.openapi({ description: "Fecha operativa de la tanda." }),
    direction: RideRequestDirection,
    providerId: ProviderId.optional().openapi({
        description: "Opcional (R1.5): sin proveedor, la tanda se reparte entre todos los proveedores de combis segun la politica de cada persona y el default del tenant; con proveedor, se arma solo ese (re-armar uno).",
    }),
    anchor: Instant.optional().openapi({
        description: "Opcional (PB-16): el ancla del grupo (shift_instant). Con ancla, se arma solo ese grupo (un turno); sin ancla, todos los de la fecha y sentido.",
    }),
    scope: RoutingScope.optional(),
})
    .openapi("RoutingRequest", {
    description: "Pedido de armado: las solicitudes pendientes de planta + fecha + direction (opcionalmente un grupo y solo los grupos cerrados), repartidas entre proveedores o forzadas a uno. Re-armar es seguro: las solicitudes ya asignadas se ignoran y solo se arma lo nuevo.",
});
export const UnassignedReason = z
    .enum(["direct_journey_over_max", "duplicate_record", "no_origin"])
    .openapi("UnassignedReason", {
    description: "Por que una solicitud quedo sin asignar: su trayecto directo ya supera el tope, el legajo aparece dos veces en la tanda, o no tiene origin.",
});
export const UnassignedRideRequest = z
    .object({
    requestId: RideRequestId,
    record: RecordId,
    reason: UnassignedReason,
})
    .openapi("UnassignedRideRequest", { description: "Solicitud que el armado no pudo asignar." });
export const OutOfAreaRideRequest = z
    .object({
    requestId: RideRequestId,
    record: RecordId,
    stopId: StopId,
})
    .openapi("OutOfAreaRideRequest", {
    description: "Solicitud sobre parada fija (colectivo): fuera del alcance del armado puerta a puerta en R1.",
});
export const AllocationRejectionReason = z
    .enum(["preferred_provider_without_fleet", "default_provider_without_fleet", "no_default_provider", "no_eligible_provider"])
    .openapi("AllocationRejectionReason", {
    description: "Por que el reparto no ubico una solicitud en ningun proveedor: su proveedor preferido no tiene combis, el default del tenant no tiene combis, hay varios proveedores y ni la persona ni el tenant nombran uno, o (desde 0.6.33, AT-8) ningun proveedor habilitado en la planta tiene una unidad en servicio de ese tipo (proveedores deshabilitados, unidades bloqueadas o de baja, o ninguno cargado). Este ultimo se evalua antes que los otros.",
});
export const AllocationRejection = z
    .object({
    requestId: RideRequestId,
    record: RecordId,
    reason: AllocationRejectionReason,
    providerId: ProviderId.optional().openapi({
        description: "El proveedor que se intento (preferido o default), si hubo uno.",
    }),
})
    .openapi("AllocationRejection", {
    description: "Solicitud que el reparto entre proveedores no pudo ubicar: queda pendiente.",
});
export const ProviderSummary = z
    .object({
    providerId: ProviderId,
    tripsCreated: Integer.min(0),
    passengers: Integer.min(0),
    totalKm: Decimal.min(0),
    maxJourneyDurationMin: Decimal.min(0),
    unassigned: Integer.min(0),
    outOfArea: Integer.min(0),
})
    .openapi("ProviderSummary", {
    description: "Los numeros del armado de UN proveedor dentro de la tanda repartida.",
});
export const SummaryTrip = z
    .object({
    tripId: TripId,
    providerId: ProviderId,
    vehicleId: z.string().min(1),
    batch: Integer.min(0).openapi({ description: "Numero de tanda dentro del armado." }),
    stops: Integer.min(0),
    passengers: Integer.min(0),
    departure: Instant.openapi({ description: "Hora estimada de la primera parada." }),
    arrival: Instant.openapi({ description: "Hora estimada de la ultima parada." }),
    withItinerary: z.boolean().openapi({
        description: "true si el ruteo por calles estuvo disponible al armar.",
    }),
})
    .openapi("SummaryTrip", { description: "Un viaje creado por el armado." });
export const RoutingSummary = z
    .object({
    siteId: SiteId,
    date: CalendarDate,
    direction: RideRequestDirection,
    providerId: ProviderId.optional().openapi({
        description: "Solo cuando el pedido forzo un proveedor; ausente en el reparto.",
    }),
    byProvider: z.array(ProviderSummary).openapi({
        description: "Los numeros por proveedor armado, ordenados por providerId.",
    }),
    allocationRejections: z.array(AllocationRejection),
    tripsCreated: Integer.min(0),
    passengers: Integer.min(0),
    totalKm: Decimal.min(0),
    maxJourneyDurationMin: Decimal.min(0).openapi({
        description: "Trayecto mas largo de un pasajero en minutos; nunca supera el tope de la politica.",
    }),
    unassigned: Integer.min(0),
    outOfArea: Integer.min(0),
    trips: z.array(SummaryTrip),
    unassignedDetail: z.array(UnassignedRideRequest),
    outOfAreaDetail: z.array(OutOfAreaRideRequest),
})
    .openapi("RoutingSummary", {
    description: "Los numeros del armado, reales y persistidos: viajes, pasajeros, km y descartes. Cuentan solo lo que armo esta corrida: al re-armar, las solicitudes que ya estaban asignadas no suman.",
});
export const RoutingResponse = z
    .object({ summary: RoutingSummary })
    .openapi("RoutingResponse", { description: "Respuesta 201 del armado." });
export const RoutedTripListItem = z
    .object({
    tripId: TripId,
    vehicleId: z.string().min(1).nullable().openapi({
        description: "Vehiculo asignado; null cuando el viaje quedo sin vehiculo (distinto de desconocido).",
    }),
    providerId: ProviderId,
    status: TripStatus,
    stops: Integer.min(0),
    passengers: Integer.min(0),
})
    .openapi("RoutedTripListItem", { description: "Un viaje con origin armado, resumido." });
export const RoutingList = z
    .object({
    siteId: SiteId,
    date: CalendarDate,
    trips: z.array(RoutedTripListItem),
})
    .openapi("RoutingList", { description: "Lo armado un dia en la planta." });
export const RoutingListQuery = z
    .object({ date: CalendarDate })
    .openapi("RoutingListQuery", { description: "Dia a listar (obligatorio)." });
//# sourceMappingURL=routing.js.map